import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authJsonHeaders, fetchCsrfToken, fetchSession } from '../utils/authClient';

const ConnectContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ConnectCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 700px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-weight: 500;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff88;
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  }

  &::placeholder {
    color: #888888;
  }

  &.error {
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

const SearchButton = styled.button`
  background: linear-gradient(45deg, #00d4ff, #0099cc);
  color: #000000;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConnectButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 255, 136, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid #ff4444;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #ff4444;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SuccessMessage = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00ff88;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SearchResults = styled.div`
  margin-top: 2rem;
`;

const InmateProfileCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00ff88;
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const InmateName = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const InmateDetails = styled.div`
  color: #cccccc;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const InmateId = styled.span`
  color: #00ff88;
  font-weight: bold;
`;

const FacilityInfo = styled.div`
  margin-top: 0.5rem;
  color: #888888;
`;

const StatusBadge = styled.span`
  background: ${props => props.status === 'active' ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 68, 68, 0.2)'};
  color: ${props => props.status === 'active' ? '#00ff88' : '#ff4444'};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  margin-left: 1rem;
`;

const NoResults = styled.div`
  text-align: center;
  color: #888888;
  padding: 2rem;
  font-style: italic;
`;

const SearchSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SearchTitle = styled.h3`
  color: #00ff88;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  color: #00ff88;
  padding: 2rem;
  font-size: 1.1rem;
`;

const ConnectToInmate = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchSession();
        if (session.network || session.error) {
          setAuthError(session.message || 'Unable to verify sign-in.');
          return;
        }
        if (session.status === 401) {
          navigate('/signin', { state: { from: '/connect-to-inmate' } });
          return;
        }
        if (session.status === 403) {
          setAuthError(session.message || 'This account cannot connect to an inmate yet.');
          return;
        }
        setUser(session.user);
      } catch (error) {
        setAuthError('Unable to verify sign-in.');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setSearchResults([]);
    setErrors({});
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setErrors({ search: 'Please enter a name or ID to search' });
      return;
    }

    setIsSearching(true);
    setErrors({});

    try {
      const response = await fetch(`/api/search-inmates?q=${encodeURIComponent(searchQuery)}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.inmates || []);
        
        if (data.inmates && data.inmates.length === 0) {
          setErrors({ search: `No inmates found matching "${searchQuery}". Try searching by full name or exact inmate ID.` });
        }
      } else {
        const errorData = await response.json();
        setErrors({ search: errorData.message || 'Search failed. Please try again.' });
      }
    } catch (error) {
      console.error('Search error:', error);
      setErrors({ search: 'Network error. Please try again.' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleConnect = async (inmate) => {
    if (!user) {
      setErrors({ submit: 'You must be signed in to connect to an inmate.' });
      return;
    }

    setIsConnecting(true);
    setErrors({});

    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch('/api/connect-to-inmate', {
        method: 'POST',
        credentials: 'include',
        headers: authJsonHeaders(csrfToken),
        body: JSON.stringify({
          inmateId: inmate.inmateId,
          inmateName: inmate.inmateName,
          facilityName: inmate.facilityName,
          state: inmate.state,
          relationship: 'Friend'
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.message || 'Connection failed' });
      }
    } catch (error) {
      console.error('Connect error:', error);
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (authError) {
    return (
      <ConnectContainer>
        <ConnectCard>
          <Title>Connect to Inmate</Title>
          <ErrorMessage>{authError}</ErrorMessage>
        </ConnectCard>
      </ConnectContainer>
    );
  }

  if (!user) {
    return (
      <ConnectContainer>
        <ConnectCard>
          <LoadingSpinner>Loading...</LoadingSpinner>
        </ConnectCard>
      </ConnectContainer>
    );
  }

  return (
    <ConnectContainer>
      <ConnectCard>
        <Title>Connect to Inmate</Title>
        <Subtitle>Search for an inmate and connect your account to receive calls and messages</Subtitle>

        <InfoBox>
          <strong>How it works:</strong> Search for an inmate by their full name or exact inmate ID number. 
          View their profile and click "Connect" to establish a connection. Once connected, you'll be able to receive calls and messages from their Skylink number.
        </InfoBox>

        {success && (
          <SuccessMessage>
            ✅ Successfully connected! You can now receive calls and messages from this inmate.
          </SuccessMessage>
        )}

        {errors.submit && (
          <ErrorMessage>❌ {errors.submit}</ErrorMessage>
        )}

        <SearchSection>
          <SearchTitle>🔍 Search for Inmate</SearchTitle>
          <FormGroup>
            <Label htmlFor="searchQuery">Inmate Name or ID</Label>
            <Input
              type="text"
              id="searchQuery"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter inmate name or exact inmate ID (e.g., John Smith or 12345)"
            />
            {errors.search && <ErrorMessage>{errors.search}</ErrorMessage>}
          </FormGroup>
          
          <SearchButton 
            type="button" 
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </SearchButton>
        </SearchSection>

        {isSearching && (
          <LoadingSpinner>Searching for inmates...</LoadingSpinner>
        )}

        {searchResults.length > 0 && (
          <SearchResults>
            <SearchTitle>📋 Search Results</SearchTitle>
            {searchResults.map(inmate => (
              <InmateProfileCard key={inmate.id}>
                <InmateName>
                  {inmate.inmateName}
                  <StatusBadge status={inmate.status}>
                    {inmate.status === 'active' ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </InmateName>
                <InmateDetails>
                  <div><strong>ID:</strong> <InmateId>{inmate.inmateId}</InmateId></div>
                  <FacilityInfo>
                    <strong>Facility:</strong> {inmate.facilityName}, {inmate.state}
                  </FacilityInfo>
                </InmateDetails>
                <ConnectButton 
                  onClick={() => handleConnect(inmate)}
                  disabled={isConnecting}
                >
                  {isConnecting ? 'Connecting...' : 'Connect'}
                </ConnectButton>
              </InmateProfileCard>
            ))}
          </SearchResults>
        )}
      </ConnectCard>
    </ConnectContainer>
  );
};

export default ConnectToInmate; 